import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Alert,
  Stepper,
  Step,
  StepLabel,
  TextField,
  Typography,
  CircularProgress
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker, TimePicker } from '@mui/x-date-pickers';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import CalendarMonth from '@mui/icons-material/CalendarMonth';
import MedicalServices from '@mui/icons-material/MedicalServices';
import Close from '@mui/icons-material/Close';
import Search from '@mui/icons-material/Search';
import athenaService from '../services/athenaService';
import AppointmentTypeMapper from '../services/appointmentTypeMapper';
import PatientForm from './PatientForm';

const AppointmentForm = ({ open, onClose, onSave }) => {
  // Helper function to normalize city names for consistent formatting
  const toCamelCase = (str) => {
    if (!str) return '';
    return str
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  // More granular loading states for different fields
  const [loadingProviders, setLoadingProviders] = useState(false);
  const [loadingReasons, setLoadingReasons] = useState(false);
  const [providersReady, setProvidersReady] = useState(false);
  const [reasonsReady, setReasonsReady] = useState(false);
  const [loadingAppointmentTypes, setLoadingAppointmentTypes] = useState(false);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [providers, setProviders] = useState([]);
  const [appointmentTypes, setAppointmentTypes] = useState([]);
  const [appointmentReasons, setAppointmentReasons] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [patients, setPatients] = useState([]);
  const [patientSearch, setPatientSearch] = useState({ firstName: '', lastName: '', dob: null });
  const [patientSearchResults, setPatientSearchResults] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [isNewPatient, setIsNewPatient] = useState(false);
  const [patientFormOpen, setPatientFormOpen] = useState(false);
  
  // New booking mode and patient type states (matching AppointmentScheduler.js)
  const [bookingType, setBookingType] = useState('appointment'); // 'appointment' or 'iv'
  const [patientType, setPatientType] = useState('new'); // 'new' or 'existing' - default to 'new'
  
  // Add state for locations (grouped departments)
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [departmentsByLocation, setDepartmentsByLocation] = useState({});
  const [selectedDepartmentId, setSelectedDepartmentId] = useState('');
  const [formData, setFormData] = useState({
    departmentId: '',
    providerId: '',
    appointmentTypeId: '',
    reasonId: '',
    appointmentDate: null,
    appointmentTime: null,
    patientId: '',
    notes: '',
    duration: 30,
    appointmentId: ''
  });
  
  const [errors, setErrors] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    const initialize = async () => {
      setLoading(true);
      try {
        // Fetch departments
        const departmentsList = await athenaService.getDepartments();
        
        // Filter out departments with empty providerlist
        const filteredDepts = departmentsList.filter(dept => 
          dept.providerlist && dept.providerlist.length > 0
        );
        
        console.log('AppointmentForm: Departments with providers:', filteredDepts);
        setDepartments(filteredDepts);
        
        // Group departments by location (city) - matching AppointmentScheduler.js
        const deptsByLocation = {};
        const locationList = [];
        
        filteredDepts.forEach(dept => {
          const locationName = toCamelCase(dept.city) || 'Unknown';
          
          // Initialize location group if it doesn't exist
          if (!deptsByLocation[locationName]) {
            deptsByLocation[locationName] = [];
            locationList.push({
              name: locationName,
              departments: []
            });
          }
          
          // Add department to its location group
          deptsByLocation[locationName].push(dept);
          
          // Update the location list with this department
          const locationIndex = locationList.findIndex(loc => loc.name === locationName);
          if (locationIndex >= 0) {
            locationList[locationIndex].departments.push(dept);
          }
        });
        
        console.log("Departments grouped by location:", deptsByLocation);
        console.log("Location list:", locationList);
        
        setLocations(locationList);
        setDepartmentsByLocation(deptsByLocation);

        // Check if we have patient information from session storage (for booking from patient list)
        const bookingPatientId = sessionStorage.getItem('bookingPatientId');
        const bookingFirstName = sessionStorage.getItem('bookingPatientFirstName');
        const bookingLastName = sessionStorage.getItem('bookingPatientLastName');
        const bookingDob = sessionStorage.getItem('bookingPatientDob');
        
        // If we have patient info, pre-fill the form and set the patient type to existing
        if (bookingPatientId) {
          console.log('Found patient info in sessionStorage:', {
            id: bookingPatientId,
            firstName: bookingFirstName,
            lastName: bookingLastName,
            dob: bookingDob
          });

          // IMPORTANT: Set patient type to existing first, before updating other state
          // This ensures the UI is in the right mode before data is filled
          setPatientType('existing');
          
          // Use a slight delay to ensure patientType has been set before updating the search fields
          setTimeout(() => {
            // Update form data with patient ID
            setFormData(prev => ({
              ...prev,
              patientId: bookingPatientId
            }));
            
            // Pre-fill the patient search fields
            const patientSearchData = {
              firstName: bookingFirstName || '',
              lastName: bookingLastName || '',
              dob: bookingDob ? new Date(bookingDob) : null
            };
            
            console.log('Setting patient search data:', patientSearchData);
            setPatientSearch(patientSearchData);
            
            // Set active step to 0 (patient selection) to show the pre-filled info
            setActiveStep(0);
          }, 100);
          
          // Clear the session storage
          sessionStorage.removeItem('bookingPatientId');
          sessionStorage.removeItem('bookingPatientFirstName');
          sessionStorage.removeItem('bookingPatientLastName');
          sessionStorage.removeItem('bookingPatientDob');
        }

        // Get patients directly from appointments instead of searching
        try {
          // This method extracts patient data from existing appointments
          const appointmentPatients = await athenaService.getPatientsFromAppointments();
          if (appointmentPatients && appointmentPatients.length > 0) {
            console.log(`Using ${appointmentPatients.length} patients from appointments`);
            setPatients(appointmentPatients);
          } else {
            // Fallback to localStorage patients if needed
            console.log('No appointment patients found, using localStorage');
            const localPatients = JSON.parse(localStorage.getItem('patients') || '[]');
            setPatients(localPatients);
          }
        } catch (patientError) {
          console.error("Error loading patients:", patientError);
          // Graceful fallback - continue with empty patients list
          setPatients([]);
        }
      } catch (error) {
        console.error("Error initializing appointment form:", error);
        
        // Extract detailed error message if available
        let errorMessage = 'Could not load appointment form data';
        
        if (error.response?.data?.error) {
          // Handle structured error from API
          const apiError = error.response.data.error;
          errorMessage = apiError.detailedmessage || apiError.error || apiError.message || errorMessage;
        } else if (error.response?.data?.message) {
          // Handle general API error message
          errorMessage = error.response.data.message;
        } else if (error.message) {
          // Use the error's message property
          errorMessage = error.message;
        }
        
        setSnackbar({
          open: true,
          message: `Failed to load appointment form data: ${errorMessage}`,
          severity: 'error'
        });
      } finally {
        setLoading(false);
      }
    };

    if (open) {
      initialize();
    }
  }, [open]);

  // Function to load providers for a department, similar to AppointmentScheduler.js
  const loadProvidersForDepartment = async (departmentId) => {
    console.log("Loading providers for department:", departmentId);
    if (!departmentId) return [];
    
    try {
      setLoadingProviders(true);
      setProvidersReady(false);
      
      // Find the selected department to get its providerlist
      const department = departments.find(d => 
        d.departmentid === departmentId || 
        d.departmentid === parseInt(departmentId)
      );
      
      // Get all providers for the department
      const provs = await athenaService.getProviders(parseInt(departmentId));
      
      // Filter out non-person entities first
      let humanProviders = provs.filter(p => p.entitytype === 'Person');
      
      // Apply initial filtering based on booking type
      let filteredProvs = [...humanProviders];
      
      // For regular appointments, remove IV providers
      if (bookingType === 'appointment') {
        filteredProvs = filteredProvs.filter(p => 
          p.displayname !== 'IV_HOME' && p.displayname !== 'IV_OFFICE'
        );
      } 
      // For IV service, only include IV_OFFICE provider (but since we filtered non-person entities, handle this differently)
      else if (bookingType === 'iv') {
        // For IV service, we need to include the IV_OFFICE provider even though it's non-person
        const ivOfficeProvider = provs.find(p => p.displayname === 'IV_OFFICE');
        if (ivOfficeProvider) {
          filteredProvs = [ivOfficeProvider];
        } else {
          filteredProvs = [];
        }
      }
      
      // Format provider names consistently for human providers
      filteredProvs = filteredProvs.map(provider => {
        if (provider.entitytype === 'Person') {
          const firstName = provider.firstname || '';
          const lastName = provider.lastname || '';
          const degree = provider.providertypeid || '';
          
          // Create formatted display name: "FirstName LastName, DEGREE"
          let formattedName = '';
          if (firstName && lastName) {
            formattedName = `${firstName} ${lastName}`;
            if (degree) {
              formattedName += `, ${degree}`;
            }
          } else if (provider.displayname) {
            // Fallback to displayname if first/last not available
            formattedName = provider.displayname;
          }
          
          return {
            ...provider,
            formattedDisplayName: formattedName
          };
        }
        // For non-person entities (like IV_OFFICE), keep original displayname
        return {
          ...provider,
          formattedDisplayName: provider.displayname || provider.lastname || ''
        };
      });
      
      // If department has a providerlist, further filter providers to only include those in the list
      if (department && department.providerlist && department.providerlist.length > 0) {
        filteredProvs = filteredProvs.filter(p => 
          department.providerlist.includes(p.providerid.toString())
        );
      }
      
      console.log(`Found ${filteredProvs.length} providers after filtering (${humanProviders.length} human, ${provs.length} total)`);
      setProviders(filteredProvs || []);
      setProvidersReady(true);
      return filteredProvs || [];
    } catch (err) {
      console.error(`Error loading providers for department ${departmentId}:`, err);
      
      // Extract detailed error message if available
      let errorMessage = 'Could not load providers';
      
      if (err.response?.data?.error) {
        // Handle structured error from API
        const apiError = err.response.data.error;
        errorMessage = apiError.detailedmessage || apiError.error || apiError.message || errorMessage;
      } else if (err.response?.data?.message) {
        // Handle general API error message
        errorMessage = err.response.data.message;
      } else if (err.message) {
        // Use the error's message property
        errorMessage = err.message;
      }
      
      // Show error in snackbar
      setSnackbar({
        open: true,
        message: `Failed to load providers: ${errorMessage}`,
        severity: 'error'
      });
      
      setProviders([]);
      setProvidersReady(false);
      return [];
    } finally {
      setLoadingProviders(false);
    }
  };
  
  // Function to load providers for all departments in a location (exactly matching AppointmentScheduler.js)
  const loadProvidersForLocation = async (locationName) => {
    console.log("Loading providers for location:", locationName);
    if (!locationName) return;
    
    try {
      setLoadingProviders(true);
      
      // Get all departments for this location
      const locationDepartments = departmentsByLocation[locationName] || [];
      console.log(`Found ${locationDepartments.length} departments for location ${locationName}`);
      
      if (locationDepartments.length === 0) {
        setProviders([]);
        setLoadingProviders(false);
        return;
      }
      
      // To combine all providers across departments
      const allProviders = [];
      // Set to track unique provider IDs
      const allProviderIds = new Set();
      
      // First select the first department for consistency
      const firstDepartment = locationDepartments[0];
      setFormData(prev => ({
        ...prev,
        departmentId: firstDepartment.departmentid.toString()
      }));
      
      // Loop through all departments in this location
      for (const dept of locationDepartments) {
        // Load providers for each department
        const departmentProviders = await athenaService.getProviders(parseInt(dept.departmentid));
        
        // Filter and add to all providers collection
        for (const provider of departmentProviders) {
          // Only add if not already in the collection
          if (!allProviderIds.has(provider.providerid)) {
            allProviderIds.add(provider.providerid);
            allProviders.push(provider);
          }
        }
      }
      
      // Apply filtering based on booking type
      let filteredProviders = [...allProviders];
      
      // Filter providers based on booking type
      if (bookingType === 'appointment') {
        // For regular appointments, remove IV providers
        filteredProviders = filteredProviders.filter(p => 
          p.displayname !== 'IV_HOME' && p.displayname !== 'IV_OFFICE'
        );
      } else if (bookingType === 'iv') {
        // For IV booking, only keep IV_OFFICE provider
        const ivOfficeProvider = filteredProviders.find(p => p.displayname === 'IV_OFFICE');
        if (ivOfficeProvider) {
          filteredProviders = [ivOfficeProvider];
          // Automatically select the IV_OFFICE provider
          setFormData(prev => ({
            ...prev,
            providerId: ivOfficeProvider.providerid.toString()
          }));
          
          // Then load appointment types for this provider using the first department
          setTimeout(() => {
            loadAppointmentTypes(locationDepartments[0].departmentid, ivOfficeProvider.providerid);
          }, 100);
        }
      }
      
      // Set the combined list of providers
      setProviders(filteredProviders);
      
    } catch (err) {
      console.error("Error loading providers for location:", err);
      setSnackbar({
        open: true,
        message: "Failed to load providers. Please try again.",
        severity: "error"
      });
      setProviders([]);
    } finally {
      setLoadingProviders(false);
    }
  };
  
  // Update providers when department is selected
  useEffect(() => {
    if (formData.departmentId) {
      loadProvidersForDepartment(formData.departmentId);
    }
  }, [formData.departmentId]);

  // Previous loadProviders useEffect has been replaced by loadProvidersForDepartment

  // Dedicated loadAppointmentReasons function matching AppointmentScheduler.js
  const loadAppointmentReasons = async (departmentId, providerId, patientTypeOverride) => {
    if (!departmentId || !providerId) return;
    
    // Use passed-in patient type if provided, otherwise use state
    const currentPatientType = patientTypeOverride || patientType;
    
    try {
      setLoadingReasons(true);
      setReasonsReady(false);
      
      // Skip for IV bookings - the reasons are set by loadAppointmentTypes instead
      if (bookingType === 'iv') {
        console.log('IV booking - reasons are set by loadAppointmentTypes instead');
        // This will trigger loadAppointmentTypes which handles both appointment types and reasons for IV
        await loadAppointmentTypes(departmentId, providerId, currentPatientType);
        setLoading(false);
        return;
      }
      
      let reasons = [];
      let defaultReasons = [];
      
      console.log(`Loading appointment reasons for patient type: ${currentPatientType}`);
      
      if (currentPatientType === 'new') {
        // Load new patient reasons
        const response = await athenaService.getAppointmentReasonforNewPatient(departmentId, providerId);
        console.log("New patient reasons response:", response);
        
        if (response && response.patientappointmentreasons && response.patientappointmentreasons.length > 0) {
          // Filter to only show "New Patient" reasons
          reasons = response.patientappointmentreasons.filter(reason => 
            reason.reason === "New Patient"
          );
        }
        
        // Default reasons for new patients
        defaultReasons = [
          { reasonid: '11', reason: "New Patient", description: "I am a new patient and I would like to schedule my first visit." }
        ];
        
      } else if (currentPatientType === 'existing') {
        // Load existing patient reasons
        const response = await athenaService.getAppointmentReasonforExistingPatient(departmentId, providerId);
        console.log("Existing patient reasons response:", response);
        
        let followUpReasons = [];
        let telehealthReason = null;
        
        if (response && response.patientappointmentreasons && response.patientappointmentreasons.length > 0) {
          // Filter to get "Follow Up" reasons
          followUpReasons = response.patientappointmentreasons.filter(reason => 
            reason.reason === "Follow Up"
          );
          
          // Check if Telehealth is already in the API response
          telehealthReason = response.patientappointmentreasons.find(reason => 
            reason.reason === "Telehealth"
          );
        }
        
        // Always ensure TeleHealth is available for existing patients
        if (!telehealthReason) {
          telehealthReason = { 
            reasonid: 'default_telehealth', 
            reason: "Telehealth", 
            description: "I would like to schedule a telehealth appointment." 
          };
        }
        
        // Combine Follow Up reasons with Telehealth
        reasons = [...followUpReasons, telehealthReason];
        
        // Default reasons for existing patients (Follow Up + Telehealth) - used as fallback only
        defaultReasons = [
          { reasonid: '4', reason: "Follow Up", description: "I need to follow up with my provider about a condition or other issue I have." },
          { reasonid: 'default_telehealth', reason: "Telehealth", description: "I would like to schedule a telehealth appointment." }
        ];
      }
      
      // Use API reasons if available, otherwise use defaults
      const finalReasons = reasons.length > 0 ? reasons : defaultReasons;
      
      console.log(`Setting appointment reasons for ${currentPatientType} patient:`, finalReasons);
      setAppointmentReasons(finalReasons);
      setReasonsReady(true);
      
      // Auto-select first reason if available
      if (finalReasons.length > 0) {
        setFormData(prev => ({
          ...prev,
          reasonId: finalReasons[0].reasonid
        }));
        
        // After setting a reason, load the appointment types that match this reason
        console.log('Auto-loading appointment types for selected reason:', finalReasons[0].reason);
        await loadAppointmentTypes(departmentId, providerId, currentPatientType);
      }
    } catch (err) {
      console.error("Error loading appointment reasons:", err);
      
      // Provide fallback defaults based on patient type
      const fallbackReasons = currentPatientType === 'new' 
        ? [{ reasonid: '11', reason: "New Patient", description: "I am a new patient and I would like to schedule my first visit." }]
        : [
            { reasonid: '4', reason: "Follow Up", description: "I need to follow up with my provider about a condition or other issue I have." },
            { reasonid: 'default_telehealth', reason: "Telehealth", description: "I would like to schedule a telehealth appointment." }
          ];
      
      setAppointmentReasons(fallbackReasons);
      
      // Auto-select first fallback reason
      if (fallbackReasons.length > 0) {
        setFormData(prev => ({
          ...prev,
          reasonId: fallbackReasons[0].reasonid
        }));
      }
    } finally {
      setLoadingReasons(false);
      setLoadingAppointmentTypes(false);
    }
  };

  // Updated loadAppointmentTypes function to handle appointment type and reason selection properly
  const loadAppointmentTypes = async (departmentId, providerId, patientTypeOverride) => {
    if (!departmentId || !providerId) return;
    
    const currentPatientType = patientTypeOverride || patientType;
    
    setLoadingAppointmentTypes(true);
    try {
      const appointmentTypesData = await athenaService.getAppointmentTypes(
        parseInt(departmentId),
        parseInt(providerId)
      );
      
      console.log('Book Appointment - All appointment types for department/provider:', appointmentTypesData);
      
      // Extract types array
      let typesArray = [];
      if (Array.isArray(appointmentTypesData)) {
        typesArray = appointmentTypesData;
      } else {
        // This handles the case where the API returns an object with appointmenttypes property
        typesArray = appointmentTypesData?.appointmenttypes || [];
      }
      
      // Filter appointment types based on booking type and patient type
      let filteredTypes = [...typesArray];
      
      // Special handling for IV booking type
      if (bookingType === 'iv') {
        // For IV booking, ignore patient type and filter to only show INJECTION/IV types
        console.log('Filtering for IV service types only (ignoring patient type)');
        const ivKeywords = ['INJECTION', 'IV', 'INFUSION'];
        
        filteredTypes = filteredTypes.filter(type => {
          const typeName = (type.name || '').toUpperCase();
          return ivKeywords.some(keyword => typeName.includes(keyword));
        });
        
        // If no IV types found from API, use default IV appointment type
        if (filteredTypes.length === 0) {
          const defaultIvType = {
            "appointmenttypeid": 9,
            "group": false,
            "templatetypeonly": false,
            "name": "INJECTION/ IV",
            "shortname": "INJ",
            "duration": 30,
            "patientdisplayname": "INJECTION/IV",
            "patient": true,
            "generic": false,
            "createencounteroncheckin": false
          };
          filteredTypes = [defaultIvType];
          console.log('No IV types found from API, using default IV appointment type');
        }
        
        console.log('IV service filtered types:', filteredTypes.map(t => t.name));
        
        // Convert appointment types to reason format for the dropdown
        const appointmentTypeReasons = filteredTypes.map(type => ({
          reasonid: type.appointmenttypeid,
          reason: type.name,
          description: `${type.name} appointment (${type.duration} minutes)`
        }));
        
        // Set the appointment types as reasons for IV service
        setAppointmentReasons(appointmentTypeReasons);
        console.log('Set appointment reasons for IV service:', appointmentTypeReasons);
        
        // Prefer INJECTION/IV specifically if available
        const exactIvType = filteredTypes.find(type => 
          type.name.toUpperCase().includes('INJECTION/IV') || 
          type.name.toUpperCase().includes('INJECTION / IV')
        );
        
        let selectedTypeId;
        if (exactIvType) {
          selectedTypeId = exactIvType.appointmenttypeid;
          setFormData(prev => ({
            ...prev,
            reasonId: selectedTypeId.toString(),
            appointmentTypeId: selectedTypeId.toString()
          }));
          console.log('Auto-selected INJECTION/IV appointment type:', exactIvType.name);
        } else if (filteredTypes.length > 0) {
          // Otherwise just use the first filtered type
          selectedTypeId = filteredTypes[0].appointmenttypeid;
          setFormData(prev => ({
            ...prev,
            reasonId: selectedTypeId.toString(),
            appointmentTypeId: selectedTypeId.toString()
          }));
          console.log('Auto-selected first IV appointment type:', filteredTypes[0].name);
        }
      } 
      // Regular appointment type handling
      else if (bookingType === 'appointment') {
        // For regular appointments, filter based on patient type
        console.log(`Filtering appointment types - patient type: ${currentPatientType}`);
        
        if (currentPatientType === 'new') {
          // For new patients, only show NEW PATIENT appointment types
          filteredTypes = filteredTypes.filter(type => {
            const typeName = (type.name || '').toUpperCase();
            return typeName.includes('NEW PATIENT') || typeName.includes('NEW PAT');
          });
          console.log('Filtered new patient types:', filteredTypes.map(t => t.name));
          
        } else if (currentPatientType === 'existing') {
          // For existing patients, ONLY show FOLLOW UP and TELEHEALTH types
          filteredTypes = filteredTypes.filter(type => {
            const typeName = (type.name || '').toUpperCase();
            return typeName.includes('FOLLOW UP') || typeName.includes('TELEHEALTH');
          });
          console.log('Filtered existing patient types:', filteredTypes.map(t => t.name));
        }
        
        // For regular appointments, set the filtered types
        setAppointmentTypes(filteredTypes);
        
        // Auto-select first type if available
        if (filteredTypes.length > 0) {
          setFormData(prev => ({
            ...prev,
            appointmentTypeId: filteredTypes[0].appointmenttypeid.toString()
          }));
        }
      }
    } catch (error) {
      console.error("Error loading appointment types:", error);
      setAppointmentTypes([]);
    } finally {
      setLoadingAppointmentTypes(false);
    }
  };
  
  // Use effect to call loadAppointmentTypes when dependencies change
  useEffect(() => {
    if (formData.departmentId && formData.providerId) {
      loadAppointmentTypes(formData.departmentId, formData.providerId);
    }
  }, [formData.departmentId, formData.providerId, patientType, bookingType]);

  // New useEffect to load appointment reasons when provider or patient type changes
  useEffect(() => {
    if (formData.departmentId && formData.providerId) {
      loadAppointmentReasons(formData.departmentId, formData.providerId, patientType);
    }
  }, [formData.departmentId, formData.providerId, patientType]);

  // Updated loadAvailableSlots function that can be called directly
  const loadAvailableSlots = async () => {
    // For regular appointments, we need department, provider and reason ID
    // For IV services, we need department, provider and appointment type ID
    if (!formData.departmentId || !formData.providerId || 
        (bookingType === 'appointment' && !formData.reasonId) ||
        (bookingType === 'iv' && !formData.appointmentTypeId)) {
      console.error('Missing required parameters for loadAvailableSlots:', { 
        departmentId: formData.departmentId, 
        providerId: formData.providerId, 
        reasonId: bookingType === 'appointment' ? formData.reasonId : 'N/A',
        appointmentTypeId: bookingType === 'iv' ? formData.appointmentTypeId : 'N/A'
      });
      return;
    }
    
    setLoadingSlots(true);
    try {
      // Get selected date or today's date
      const startDate = formData.appointmentDate || new Date();
      // Use the same date as end date to only get slots for this day
      const endDate = new Date(startDate);

      // Format dates for API
      const formattedStartDate = athenaService.formatDate(startDate);
      const formattedEndDate = athenaService.formatDate(endDate);
      
      console.log('Fetching slots with params:', {
        departmentId: parseInt(formData.departmentId),
        reasonId: formData.reasonId,
        providerId: parseInt(formData.providerId),
        startDate: formattedStartDate,
        endDate: formattedEndDate
      });
      
      let slots = [];
      
      // For regular appointments, use reason-based slot fetching
      if (bookingType === 'appointment') {
        // Use the reason-based slot finding for appointments
        slots = await athenaService.getOpenAppointmentSlotsByReason(
          parseInt(formData.departmentId),
          formData.reasonId,
          formattedStartDate,
          parseInt(formData.providerId),
          formattedEndDate
        );
        
        // Apply prioritization based on patient type for regular appointments
        slots = prioritizeSlotsByPatientType(slots);
      } 
      // For IV service, use appointment type-based slot fetching
      else if (bookingType === 'iv' && formData.appointmentTypeId) {
        // The parameter order for athenaService.getOpenAppointmentSlots is:
        // getOpenAppointmentSlots(departmentId, appointmentTypeId, startDate, providerId, endDate)
        slots = await athenaService.getOpenAppointmentSlots(
          parseInt(formData.departmentId),
          parseInt(formData.appointmentTypeId),
          formattedStartDate,
          parseInt(formData.providerId),
          formattedEndDate
        );
      }
      
      console.log(`Found ${slots.length} slots for the selected criteria`);
      
      // Get the selected appointment type to access its duration
      if (slots.length > 0 && slots[0].duration) {
        // If slots have duration info in the response, use that
        const duration = slots[0].duration;
        console.log(`Filtering slots based on duration from response: ${duration} minutes`);
        const filteredSlots = slots;
        setAvailableSlots(filteredSlots);
      } 
      else if (formData.appointmentTypeId) {
        // Try to get duration from appointment type if available
        const selectedAppointmentType = appointmentTypes.find(
          type => type.appointmenttypeid == formData.appointmentTypeId
        );
        
        if (selectedAppointmentType && selectedAppointmentType.duration) {
          console.log(`Filtering slots based on appointment type duration: ${selectedAppointmentType.duration} minutes`);
          const filteredSlots = slots;
          setAvailableSlots(filteredSlots);
        } else {
          setAvailableSlots(slots);
        }
      } else {
        // No duration information available
        setAvailableSlots(slots);
      }
    } catch (error) {
      console.error("Error loading available slots:", error);
      
      // Extract detailed error message if available
      let errorMessage = 'Could not load available appointment slots';
      
      if (error.response?.data?.error) {
        // Handle structured error from API
        const apiError = error.response.data.error;
        errorMessage = apiError.detailedmessage || apiError.error || apiError.message || errorMessage;
      } else if (error.response?.data?.message) {
        // Handle general API error message
        errorMessage = error.response.data.message;
      } else if (error.message) {
        // Use the error's message property
        errorMessage = error.message;
      }
      
      setSnackbar({
        open: true,
        message: `Failed to load available slots: ${errorMessage}`,
        severity: 'error'
      });
      
      setAvailableSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };
    
  // Call loadAvailableSlots when on the date selection screen and the date changes
  useEffect(() => {
    // Only load available slots when on step 1 (date selection screen) and we have a date
    if (activeStep === 1 && formData.appointmentDate) {
      loadAvailableSlots();
    }
  }, [activeStep, formData.appointmentDate]);
  
  // Also call loadAvailableSlots when moving to step 1 via the handleNext function

  // Helper function to prioritize slots based on patient type (reuse logic from AppointmentScheduler)
  const prioritizeSlotsByPatientType = (slots) => {
    if (!slots || slots.length === 0) return [];

    console.log(`Starting slot prioritization for ${patientType} patient with ${slots.length} slots`);

    // Map slots to appropriate appointment types first (duration + patientType aware)
    const mappedSlots = AppointmentTypeMapper.mapSlotsToAppointmentTypes(
      slots,
      patientType,
      appointmentTypes
    );

    console.log(`Mapped ${slots.length} slots to appropriate appointment types`);

    // Get unique appointment type names present after mapping
    const presentTypeNames = [...new Set(mappedSlots.map(slot => slot.patientappointmenttypename))];
    console.log("Present appointment type names in mapped slots:", presentTypeNames);

    let filteredSlots = [];

    if (patientType === 'existing') {
      // Existing: Follow-up + ANY + Telehealth
      const followUpTypes = presentTypeNames.filter(name =>
        name && (name.toLowerCase().includes('follow') || name.toLowerCase().includes('any') || name.toLowerCase().includes('telehealth'))
      );
      filteredSlots = mappedSlots.filter(slot => followUpTypes.includes(slot.patientappointmenttypename));
      console.log(`Filtered from ${mappedSlots.length} to ${filteredSlots.length} slots for existing patient (Follow-Up + ANY + Telehealth)`);
    } else if (patientType === 'new') {
      // New: New Patient + ANY
      const newPatientTypes = presentTypeNames.filter(name =>
        name && (name.toLowerCase().includes('new') || name.toLowerCase().includes('any'))
      );
      filteredSlots = mappedSlots.filter(slot => newPatientTypes.includes(slot.patientappointmenttypename));
      console.log(`Filtered from ${mappedSlots.length} to ${filteredSlots.length} slots for new patient (New Patient + ANY)`);
    } else {
      filteredSlots = mappedSlots;
    }

    // Sort by date/time
    filteredSlots.sort((a, b) => {
      const dateA = new Date(`${a.date} ${a.starttime}`);
      const dateB = new Date(`${b.date} ${b.starttime}`);
      return dateA - dateB;
    });

    return filteredSlots;
  };

  // Helper function to filter slots based on appointment type duration
  const filterSlotsByDuration = (slots, duration) => {
    if (!slots.length) return [];
    
    // Sort slots by date and time
    const sortedSlots = [...slots].sort((a, b) => {
      const dateA = new Date(`${a.date} ${a.starttime}`);
      const dateB = new Date(`${b.date} ${b.starttime}`);
      return dateA - dateB;
    });
    
    const filteredSlots = [];
    const blockedSlotIndices = new Set();
    
    // First pass: identify slots that should be blocked
    for (let i = 0; i < sortedSlots.length; i++) {
      if (blockedSlotIndices.has(i)) continue; // Skip already blocked slots
      
      const currentSlot = sortedSlots[i];
      const currentStartTime = new Date(`${currentSlot.date} ${currentSlot.starttime}`);
      const currentEndTime = new Date(currentStartTime.getTime() + duration * 60000); // Convert minutes to milliseconds
      
      // Look ahead to find slots that would be blocked by this slot
      for (let j = i + 1; j < sortedSlots.length; j++) {
        const nextSlot = sortedSlots[j];
        const nextStartTime = new Date(`${nextSlot.date} ${nextSlot.starttime}`);
        
        // If the current appointment would extend beyond the start of the next slot,
        // block the next slot
        if (currentEndTime > nextStartTime) {
          console.log(`Collision detected: ${currentSlot.starttime} (ends at ${currentEndTime.toLocaleTimeString()}) collides with ${nextSlot.starttime}`);
          blockedSlotIndices.add(j); // Block the later slot
        } else {
          // No more collisions possible with sorted slots
          break;
        }
      }
    }
    
    // Second pass: add all non-blocked slots to the result
    for (let i = 0; i < sortedSlots.length; i++) {
      if (!blockedSlotIndices.has(i)) {
        filteredSlots.push(sortedSlots[i]);
      }
    }
    
    console.log(`Filtered from ${slots.length} to ${filteredSlots.length} slots based on appointment duration`);
    return filteredSlots;
  };

  // Handler for booking type changes - matching AppointmentScheduler.js
  const handleBookingTypeChange = (e) => {
    const newBookingType = e.target.value;
    console.log(`Changing booking type to: ${newBookingType}`);
    setBookingType(newBookingType);
    
    // Reset selections when booking type changes
    setFormData({
      ...formData,
      departmentId: '',
      providerId: '',
      appointmentTypeId: '',
      reasonId: ''
    });
    setSelectedLocation('');
    setProviders([]);
    setAppointmentTypes([]);
    setAppointmentReasons([]);
    setAvailableSlots([]);
  };
  
  // Handler for patient type changes - matching AppointmentScheduler.js
  const handlePatientTypeChange = (e) => {
    const newPatientType = e.target.value;
    console.log(`Changing patient type to: ${newPatientType}`);
    setPatientType(newPatientType);
    setIsNewPatient(newPatientType === 'new');
    
    // If we already have a provider selected, reload appointment types to filter by patient type
    if (formData.departmentId && formData.providerId) {
      // Reset appointment type selection
      setFormData({
        ...formData,
        appointmentTypeId: '',
        reasonId: ''
      });
      
      // Reload appointment types with the new patient type
      loadAppointmentTypes(formData.departmentId, formData.providerId, newPatientType);
    }
  };
  
  // Handler for location changes - matching AppointmentScheduler.js
  const handleLocationChange = async (e) => {
    const location = e.target.value;
    console.log("Selected location:", location);
    
    setSelectedLocation(location);
    
    // Reset dependent fields
    setFormData({
      ...formData,
      providerId: '',
      appointmentTypeId: '',
      reasonId: ''
    });
    
    setAppointmentTypes([]);
    setAppointmentReasons([]);
    setProviders([]);
    
    // If we have a selected department, load providers
    if (location && departmentsByLocation[location]) {
      const firstDepartment = departmentsByLocation[location][0];
      if (firstDepartment && firstDepartment.departmentid) {
        // Update form data with the first department in this location
        setFormData(prev => ({
          ...prev,
          departmentId: firstDepartment.departmentid
        }));
        
        loadProvidersForDepartment(firstDepartment.departmentid);
      }
    }
  };
  
  // Handle form field changes
  const handleChange = (e) => {
    const { name, value } = e.target;

    // Clear any error for this field
    setErrors(prev => ({ ...prev, [name]: '' }));
    
    // Special case for departmentId - needs to reset provider, reason, and appointment type
    if (name === 'departmentId') {
      // Reset dependent fields and load providers for this department
      setFormData(prev => ({
        ...prev,
        [name]: value,
        providerId: '',
        reasonId: '',
        appointmentTypeId: '',
        appointmentDate: null,
        appointmentTime: null
      }));
      
      // Load providers for the selected department
      if (value) {
        loadProvidersForDepartment(value);
      } else {
        setProviders([]);
        setAppointmentReasons([]);
        setAppointmentTypes([]);
        setAvailableSlots([]);
      }
    }
    // Special case for providerId - needs to reset reason and appointment type
    else if (name === 'providerId') {
      setFormData(prev => ({
        ...prev,
        [name]: value,
        reasonId: '',
        appointmentTypeId: '',
        appointmentDate: null,
        appointmentTime: null
      }));
      
      // Load appointment reasons and types for the selected provider and department
      if (value && formData.departmentId) {
        loadAppointmentReasons(formData.departmentId, value, patientType);
      } else {
        setAppointmentReasons([]);
        setAppointmentTypes([]);
        setAvailableSlots([]);
      }
    }
    // Special case for reasonId - needs to reset appointment type
    else if (name === 'reasonId') {
      setFormData(prev => ({
        ...prev,
        [name]: value,
        appointmentTypeId: '',
        appointmentDate: null,
        appointmentTime: null
      }));
      
      // Load appointment types for the selected provider, department and reason
      if (value && formData.departmentId && formData.providerId) {
        loadAppointmentTypes(formData.departmentId, formData.providerId);
      } else {
        setAppointmentTypes([]);
        setAvailableSlots([]);
      }
    }
    else if (name === 'appointmentTypeId' || name === 'appointmentDate') {
      setFormData(prev => ({
        ...prev,
        [name]: value,
        appointmentTime: null
      }));
      setAvailableSlots([]);
    }
    else {
      // For all other fields, just update the value
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleDateChange = (date) => {
    setFormData(prev => ({
      ...prev,
      appointmentDate: date,
      appointmentTime: null
    }));
    
    if (errors.appointmentDate) {
      setErrors(prev => ({
        ...prev,
        appointmentDate: null
      }));
    }
    
    setAvailableSlots([]);
  };

  const handleTimeChange = (time) => {
    setFormData(prev => ({
      ...prev,
      appointmentTime: time
    }));
    
    if (errors.appointmentTime) {
      setErrors(prev => ({
        ...prev,
        appointmentTime: null
      }));
    }
  };

  const handleSlotSelect = (slot) => {
    console.log('Selected slot:', slot); // Log the full slot object to verify it has appointmentid
    setSelectedSlot(slot);
    
    // Extract time from slot.starttime and set it
    const [hours, minutes] = slot.starttime.split(':');
    const appointmentTime = new Date();
    appointmentTime.setHours(parseInt(hours, 10));
    appointmentTime.setMinutes(parseInt(minutes, 10));

    setFormData(prev => ({
      ...prev,
      appointmentTime,
      appointmentId: slot.appointmentid // Store the appointment ID from the slot
    }));
  };

  const handleSearchPatients = async () => {
    if (!patientSearch.firstName.trim() && !patientSearch.lastName.trim() && !patientSearch.dob) return;
    
    const formatDOB = (date) => {
      if (!date) return undefined;
      const mm = String(date.getMonth() + 1).padStart(2, '0');
      const dd = String(date.getDate()).padStart(2, '0');
      const yyyy = date.getFullYear();
      return `${mm}/${dd}/${yyyy}`;
    };

    const searchParams = {
      firstName: patientSearch.firstName?.trim() || undefined,
      lastName: patientSearch.lastName?.trim() || undefined,
      dob: patientSearch.dob ? formatDOB(patientSearch.dob) : undefined,
      showinsurance: true
    };

    setLoading(true);
    try {
      // Use enhanced best match search to improve matching and include insurance data
      const results = await athenaService.searchPatientsEnhancedBestMatch(searchParams);
      console.log('Enhanced patient search results:', results);
      setPatientSearchResults(results || []);
      
      if (results && results.length === 0) {
        setSnackbar({
          open: true,
          message: 'No patients found matching your search. Try a different name or add a new patient.',
          severity: 'info'
        });
      }
    } catch (error) {
      console.error("Error searching patients (enhanced best match):", error);
      setSnackbar({
        open: true,
        message: 'Failed to search patients: ' + (error.message || 'Unknown error'),
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPatient = (patient) => {
    setFormData(prev => ({
      ...prev,
      patientId: patient.patientid
    }));
    
    // Store the selected patient object
    setSelectedPatient(patient);
    
    if (errors.patientId) {
      setErrors(prev => ({
        ...prev,
        patientId: null
      }));
    }
    
    // Clear search results
    setPatientSearchResults([]);
    setPatientSearch({
      firstName: '',
      lastName: '',
      dob: null
    });
  };

  const handleNewPatient = () => {
    setIsNewPatient(true);
    setPatientFormOpen(true);
  };

  const handleSaveNewPatient = async (patientData) => {
    try {
      setLoading(true);
      
      // Call the athenaService createPatient method to actually create the patient in Athena
      console.log('Creating new patient with data:', patientData);
      const newPatient = await athenaService.createPatient({
        ...patientData,
        departmentid: formData.departmentId // Use the selected department
      });
      
      console.log('New patient created:', newPatient);
      
      // Update form data with the new patient ID
      setFormData(prev => ({
        ...prev,
        patientId: newPatient.patientid
      }));
      
      // Store the selected patient object
      setSelectedPatient(newPatient);
      
      // Add the new patient to the patients list
      setPatients(prev => [newPatient, ...prev]);
      
      setSnackbar({
        open: true,
        message: `Patient ${patientData.firstname} ${patientData.lastname} created successfully`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Error creating patient:', error);
      setSnackbar({
        open: true,
        message: `Failed to create patient: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setLoading(false);
      setIsNewPatient(false);
      setPatientFormOpen(false);
    }
  };

  const validateStep = () => {
    const newErrors = {};
    
    if (activeStep === 0) {
      if (!formData.departmentId) newErrors.departmentId = 'Please select a department';
      if (!formData.providerId) newErrors.providerId = 'Please select a provider';
      if (!formData.appointmentTypeId) newErrors.appointmentTypeId = 'Please select an appointment type';
    } else if (activeStep === 1) {
      if (!formData.appointmentDate) newErrors.appointmentDate = 'Please select a date';
      if (!selectedSlot && !formData.appointmentTime) newErrors.appointmentTime = 'Please select a time';
    } else if (activeStep === 2) {
      if (!formData.patientId && !isNewPatient) newErrors.patientId = 'Please select a patient or create a new one';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (!validateStep()) return;
    
    // If moving from step 0 to step 1, load available appointment slots
    if (activeStep === 0) {
      // We'll load the slots as we move to the date selection screen
      loadAvailableSlots();
    }
    
    setActiveStep(prevStep => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep(prevStep => prevStep - 1);
  };

  const handleSubmit = async () => {
    if (!validateStep()) return;
    
    setLoading(true);
    try {
      // Format the appointment date correctly
      const dateObj = formData.appointmentDate;
      const formattedDate = `${(dateObj.getMonth() + 1).toString().padStart(2, '0')}/${dateObj.getDate().toString().padStart(2, '0')}/${dateObj.getFullYear()}`;
      
      // Format appointment data for the API
      const appointmentData = {
        appointmentid: selectedSlot?.appointmentid || formData.appointmentId, // Make sure we have the appointmentid
        patientid: parseInt(formData.patientId), // Convert to integer
        departmentid: formData.departmentId,
        providerid: formData.providerId,
        appointmenttypeid: selectedSlot?.appointmenttypeid, // Use appointmenttypeid from the selected slot
        reasonid: formData.reasonId,
        appointmentdate: formattedDate, // Use the correctly formatted date
        appointmenttime: formData.appointmentTime ? 
          `${formData.appointmentTime.getHours()}:${formData.appointmentTime.getMinutes().toString().padStart(2, '0')}` : 
          selectedSlot?.starttime,
        appointmentnotes: formData.notes,
        duration: selectedSlot?.duration // Use duration from the selected slot
      };
      
      console.log('Booking appointment with data:', appointmentData);
      
      // Verify we have the required parameters
      if (!appointmentData.appointmentid) {
        throw new Error('Missing appointmentid. Please select a valid time slot.');
      }
      
      // Book the appointment
      const result = await athenaService.bookAppointment(appointmentData);
      
      setSnackbar({
        open: true,
        message: 'Appointment booked successfully!',
        severity: 'success'
      });
      
      // Call the onSave callback
      onSave(result);
      
      // Reset all form data and close the dialog after a short delay
      setTimeout(() => {
        // Reset form state completely
        setFormData({
          patientId: '',
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          dob: null,
          departmentId: '',
          providerId: '',
          reasonId: '',
          appointmentTypeId: '',
          appointmentDate: null,
          appointmentTime: null,
          notes: '',
          insurance: {
            insuranceProvider: '',
            memberId: '',
            groupNumber: '',
            frontImage: null,
            backImage: null
          },
          address: {
            address1: '',
            address2: '',
            city: '',
            state: '',
            zip: ''
          }
        });
        
        // Reset other state variables
        setActiveStep(0);
        setSelectedLocation('');
        setSelectedDepartmentId('');
        setProviders([]);
        // setAvailableDates([]);
        setAvailableSlots([]);
        setSelectedSlot(null);
        // setPatientType('');
        
        // Close the dialog
        onClose();
      }, 1500);
    } catch (error) {
      console.error("Error booking appointment:", error);
      
      // Extract detailed error message if available
      let errorMessage = 'Could not complete appointment booking';
      
      if (error.response?.data?.error) {
        // Handle structured error from API
        const apiError = error.response.data.error;
        errorMessage = apiError.detailedmessage || apiError.error || apiError.message || errorMessage;
      } else if (error.response?.data?.message) {
        // Handle general API error message
        errorMessage = error.response.data.message;
      } else if (error.message) {
        // Use the error's message property
        errorMessage = error.message;
      }
      
      setSnackbar({
        open: true,
        message: `Failed to book appointment: ${errorMessage}`,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const steps = ['Appointment Details', 'Select Date & Time', 'Patient Information', 'Review & Confirm'];

  // Function to clear form selections
  const clearHandler = () => {
    setFormData({
      ...formData,
      departmentId: '',
      providerId: '',
      reasonId: '',
      appointmentTypeId: '',
      appointmentDate: null,
      appointmentTime: null
    });
    setSelectedLocation('');
    setSelectedDepartmentId('');
    setProviders([]);
    setAppointmentReasons([]);
    setAppointmentTypes([]);
    setAvailableSlots([]);
    
    // Clear any validation errors
    setErrors({});
  };
  
  // Function to search for available appointment slots
  const handleFindAvailableTimes = () => {
    if (bookingType === 'appointment') {
      // For regular appointments, we need department, provider, reason and appointment type
      if (!formData.departmentId || !formData.providerId || !formData.reasonId || !formData.appointmentTypeId) {
        setSnackbar({
          open: true,
          message: 'Please fill in all required appointment details',
          severity: 'warning'
        });
        return;
      }
    } else if (bookingType === 'iv') {
      // For IV services, we need department, provider and appointment type
      if (!formData.departmentId || !formData.providerId || !formData.appointmentTypeId) {
        setSnackbar({
          open: true,
          message: 'Please fill in all required IV service details',
          severity: 'warning'
        });
        return;
      }
    }
    
    setActiveStep(1); // Move to slot selection step
  };
  
  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box sx={{ p: 3, minHeight: "450px", display: "flex", flexDirection: "column" }}>
            <Box mb={3}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'semibold', mb: 2, color: '#383352' }}>Select Booking Type</Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant={bookingType === 'appointment' ? 'contained' : 'outlined'}
                  onClick={() => handleBookingTypeChange({ target: { value: 'appointment' } })}
                  sx={{
                    minWidth: '160px',
                    py: 1.5,
                    borderRadius: 2,
                    bgcolor: bookingType === 'appointment' ? '#383352' : 'transparent',
                    borderColor: '#383352',
                    color: bookingType === 'appointment' ? 'white' : '#383352',
                    '&:hover': {
                      bgcolor: bookingType === 'appointment' ? '#4a4568' : 'rgba(56, 51, 82, 0.04)',
                      borderColor: '#383352'
                    }
                  }}
                  startIcon={<CalendarMonth />}
                >
                  Book Appointment
                </Button>
                <Button
                  variant={bookingType === 'iv' ? 'contained' : 'outlined'}
                  onClick={() => handleBookingTypeChange({ target: { value: 'iv' } })}
                  startIcon={<MedicalServices />}
                  sx={{
                    minWidth: '160px',
                    py: 1.5,
                    borderRadius: 2,
                    bgcolor: bookingType === 'iv' ? '#383352' : 'transparent',
                    borderColor: '#383352',
                    color: bookingType === 'iv' ? 'white' : '#383352',
                    '&:hover': {
                      bgcolor: bookingType === 'iv' ? '#4a4568' : 'rgba(56, 51, 82, 0.04)',
                      borderColor: '#383352'
                    }
                  }}
                >
                  Book IV Service
                </Button>
              </Box>
            </Box>
            
            <Box mb={3}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'semibold', mb: 2, color: '#383352' }}>Patient Type</Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant={patientType === 'new' ? 'contained' : 'outlined'}
                  onClick={() => handlePatientTypeChange({ target: { value: 'new' } })}
                  sx={{
                    minWidth: '160px',
                    py: 1.5,
                    borderRadius: 2,
                    bgcolor: patientType === 'new' ? '#383352' : 'transparent',
                    borderColor: '#383352',
                    color: patientType === 'new' ? 'white' : '#383352',
                    '&:hover': {
                      bgcolor: patientType === 'new' ? '#4a4568' : 'rgba(56, 51, 82, 0.04)',
                      borderColor: '#383352'
                    }
                  }}
                >
                  New Patient
                </Button>
                <Button
                  variant={patientType === 'existing' ? 'contained' : 'outlined'}
                  onClick={() => handlePatientTypeChange({ target: { value: 'existing' } })}
                  sx={{
                    minWidth: '160px',
                    py: 1.5,
                    borderRadius: 2,
                    bgcolor: patientType === 'existing' ? '#383352' : 'transparent',
                    borderColor: '#383352',
                    color: patientType === 'existing' ? 'white' : '#383352',
                    '&:hover': {
                      bgcolor: patientType === 'existing' ? '#4a4568' : 'rgba(56, 51, 82, 0.04)',
                      borderColor: '#383352'
                    }
                  }}
                >
                  Existing Patient
                </Button>
              </Box>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ fontSize: '1.125rem', fontWeight: 'semibold', color: '#383352', mb: 1 }}>Location</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TextField 
                    select 
                    fullWidth 
                    label={selectedLocation ? '' : 'Location'} 
                    value={selectedLocation} 
                    onChange={handleLocationChange} 
                    variant="outlined" 
                    disabled={loadingLocations} 
                    sx={{ bgcolor: "#F4F0E6" }} 
                    size="small"
                    InputLabelProps={{
                      shrink: false,
                    }}
                    error={!!errors.departmentId}
                    helperText={errors.departmentId || ''}
                  >
                    <MenuItem value="">Select location</MenuItem>
                    {Object.keys(departmentsByLocation).map((locationName) => (
                      <MenuItem key={locationName} value={locationName}>
                        {locationName}
                      </MenuItem>
                    ))}
                  </TextField>
                  {loadingLocations && <CircularProgress size={24} />}
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" sx={{ fontSize: '1.125rem', fontWeight: 'semibold', color: '#383352', mb: 1 }}>Provider</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TextField 
                    select 
                    fullWidth 
                    label={formData.providerId ? '' : 'Provider'} 
                    name="providerId"
                    value={formData.providerId} 
                    onChange={handleChange} 
                    variant="outlined" 
                    disabled={!selectedLocation || !providersReady} 
                    sx={{ bgcolor: "#F4F0E6" }} 
                    size="small"
                    InputLabelProps={{
                      shrink: false,
                    }}
                    error={!!errors.providerId}
                    helperText={errors.providerId || ''}
                  >
                    <MenuItem value="">Select provider</MenuItem>
                    {providers.map((provider) => (
                      <MenuItem key={provider.providerid} value={provider.providerid}>
                        {provider.formattedDisplayName}
                      </MenuItem>
                    ))}
                  </TextField>
                  {loadingProviders && <CircularProgress size={24} />}
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" sx={{ fontSize: '1.125rem', fontWeight: 'semibold', color: '#383352', mb: 1 }}>Appointment Reason</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TextField 
                    select 
                    fullWidth 
                    label={formData.reasonId ? '' : 'Reason for Visit'} 
                    name="reasonId"
                    value={formData.reasonId || ''} 
                    onChange={handleChange} 
                    variant="outlined" 
                    disabled={!formData.providerId || loadingReasons} 
                    sx={{ bgcolor: "#F4F0E6" }} 
                    size="small"
                    InputLabelProps={{
                      shrink: false,
                    }}
                    error={!!errors.reasonId}
                    helperText={errors.reasonId || ''}
                  >
                    <MenuItem value="">Select appointment reason</MenuItem>
                    {appointmentReasons.map((reason) => (
                      <MenuItem key={reason.reasonid} value={reason.reasonid}>
                        {reason.reason}
                      </MenuItem>
                    ))}
                  </TextField>
                  {loadingReasons && <CircularProgress size={24} />}
                </Box>
              </Grid>

              {/* Appointment type dropdown removed - using reasons dropdown only */}
            </Grid>

            {/* Show notes field when all required selections are made */}
            {formData.departmentId && formData.providerId && (
              bookingType === 'iv' ? 
                // For IV bookings, show notes when reason is selected
                formData.reasonId && (
                  <Box mt={3}>
                    <TextField
                      fullWidth
                      label="Additional Notes"
                      name="notes"
                      multiline
                      rows={3}
                      value={formData.notes}
                      onChange={handleChange}
                      variant="outlined"
                    />
                  </Box>
                )
              : 
                // For regular appointments, show notes when appointment type is selected
                formData.appointmentTypeId && (
                  <Box mt={3}>
                    <TextField
                      fullWidth
                      label="Additional Notes"
                      name="notes"
                      multiline
                      rows={3}
                      value={formData.notes}
                      onChange={handleChange}
                      variant="outlined"
                    />
                  </Box>
                )
            )}
          </Box>
        );
        
      case 1:
        return (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Appointment Date"
                  value={formData.appointmentDate}
                  onChange={handleDateChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      margin="normal"
                      error={!!errors.appointmentDate}
                      helperText={errors.appointmentDate}
                      required
                    />
                  )}
                  minDate={new Date()}
                />
              </LocalizationProvider>
            </Grid>
            
            {formData.appointmentDate && availableSlots.length > 0 ? (
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  Available Time Slots
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, my: 2 }}>
                  {availableSlots.map((slot, index) => {
                    // Get duration from slot data
                    const duration = slot.duration || 30;
                    
                    // Calculate end time
                    const startTime = new Date(`${slot.date} ${slot.starttime}`);
                    const endTime = new Date(startTime.getTime() + duration * 60000);
                    const formattedEndTime = endTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                    
                    return (
                      <Button
                        key={index}
                        variant={selectedSlot?.starttime === slot.starttime ? "contained" : "outlined"}
                        onClick={() => handleSlotSelect(slot)}
                        sx={{ 
                          minWidth: 120,
                          display: 'flex',
                          flexDirection: 'column',
                          padding: '8px 16px'
                        }}
                      >
                        <Typography variant="body2">{slot.starttime}</Typography>
                        {/* <Typography variant="caption" color="text.secondary">
                          ({duration} min, ends {formattedEndTime})
                        </Typography> */}
                      </Button>
                    );
                  })}
                </Box>
                {errors.appointmentTime && (
                  <Typography color="error" variant="caption">
                    {errors.appointmentTime}
                  </Typography>
                )}
              </Grid>
            ) : formData.appointmentDate ? (
              <Grid item xs={12}>
                <Box sx={{ my: 2, textAlign: 'center' }}>
                  {loadingSlots ? (
                    <CircularProgress size={24} />
                  ) : (
                    <Typography color="textSecondary">
                      No available slots on this date. Please select another date.
                    </Typography>
                  )}
                </Box>
              </Grid>
            ) : null}
          </Grid>
        );
        
      case 2:
        return (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', mb: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleNewPatient}
                >
                  Add New Patient
                </Button>
              </Box>
              
              <Typography variant="subtitle1" gutterBottom>
                Or select an existing patient:
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <TextField
                  label="First Name"
                  value={patientSearch.firstName}
                  onChange={(e) => setPatientSearch(prev => ({ ...prev, firstName: e.target.value }))}
                  sx={{ width: '30%' }}
                />
                <TextField
                  label="Last Name"
                  value={patientSearch.lastName}
                  onChange={(e) => setPatientSearch(prev => ({ ...prev, lastName: e.target.value }))}
                  sx={{ width: '30%' }}
                />
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="Date of Birth"
                    value={patientSearch.dob}
                    onChange={(date) => setPatientSearch(prev => ({ ...prev, dob: date }))}
                    slotProps={{ textField: { sx: { width: '30%' } } }}
                  />
                </LocalizationProvider>
                <Button
                  variant="contained"
                  onClick={handleSearchPatients}
                  startIcon={<SearchIcon />}
                >
                  Search
                </Button>
              </Box>
              
              {patientSearchResults.length > 0 && (
                <Box sx={{ mt: 2, mb: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Search Results:
                  </Typography>
                  <Box sx={{ maxHeight: 200, overflow: 'auto', border: '1px solid #ddd', borderRadius: 1 }}>
                    {patientSearchResults.map(patient => (
                      <Box
                        key={patient.patientid}
                        sx={{
                          p: 2,
                          borderBottom: '1px solid #eee',
                          cursor: 'pointer',
                          '&:hover': { bgcolor: '#f5f5f5' },
                          ...(formData.patientId === patient.patientid && { bgcolor: '#e3f2fd' })
                        }}
                        onClick={() => handleSelectPatient(patient)}
                      >
                        <Typography variant="subtitle1">
                          {patient.firstname} {patient.lastname}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {patient.dob && `DOB: ${new Date(patient.dob).toLocaleDateString()}`}
                          {patient.mobilephone && ` • Phone: ${patient.mobilephone}`}
                          {patient.email && ` • Email: ${patient.email}`}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
              
              {patients.length > 0 && !patientSearchResults.length && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Recent Patients:
                  </Typography>
                  <Box sx={{ maxHeight: 200, overflow: 'auto', border: '1px solid #ddd', borderRadius: 1 }}>
                    {patients.slice(0, 5).map(patient => (
                      <Box
                        key={patient.patientid}
                        sx={{
                          p: 2,
                          borderBottom: '1px solid #eee',
                          cursor: 'pointer',
                          '&:hover': { bgcolor: '#f5f5f5' },
                          ...(formData.patientId === patient.patientid && { bgcolor: '#e3f2fd' })
                        }}
                        onClick={() => handleSelectPatient(patient)}
                      >
                        <Typography variant="subtitle1">
                          {patient.firstname} {patient.lastname}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {patient.dob && `DOB: ${new Date(patient.dob).toLocaleDateString()}`}
                          {patient.mobilephone && ` • Phone: ${patient.mobilephone}`}
                          {patient.email && ` • Email: ${patient.email}`}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
              
              {/* Display selected patient if one is chosen */}
              {formData.patientId && !patientSearchResults.length && selectedPatient && (
                <Box sx={{ mt: 3, p: 2, border: '1px solid #4caf50', borderRadius: 1, bgcolor: '#e8f5e9' }}>
                  <Typography variant="subtitle1" gutterBottom>
                    <strong>Selected Patient:</strong>
                  </Typography>
                  <Typography variant="h6">
                    {selectedPatient.firstname} {selectedPatient.lastname}
                  </Typography>
                  <Typography variant="body1" color="textSecondary">
                    {selectedPatient.dob && `DOB: ${new Date(selectedPatient.dob).toLocaleDateString()}`}
                    {selectedPatient.mobilephone && ` • Phone: ${selectedPatient.mobilephone}`}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {selectedPatient.email && `Email: ${selectedPatient.email}`}
                  </Typography>
                </Box>
              )}
              
              {errors.patientId && (
                <Typography color="error" variant="caption">
                  {errors.patientId}
                </Typography>
              )}
            </Grid>
          </Grid>
        );
        
      case 3:
        // Find the selected items to display their names
        const department = departments.find(d => d.departmentid === formData.departmentId);
        const provider = providers.find(p => p.providerid === formData.providerId);
        const appointmentType = appointmentTypes.find(t => t.appointmenttypeid === formData.appointmentTypeId);
        const reason = appointmentReasons.find(r => r.reasonid === formData.reasonId);
        // Use the selectedPatient state if available, otherwise try to find from patients array
        const patient = selectedPatient || patients.find(p => p.patientid === formData.patientId);
        
        return (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Appointment Summary
              </Typography>
              
              <Box sx={{ my: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <Typography variant="subtitle2">Location:</Typography>
                  </Grid>
                  <Grid item xs={8}>
                    <Typography>{department?.name || formData.departmentId}</Typography>
                  </Grid>
                  
                  <Grid item xs={4}>
                    <Typography variant="subtitle2">Provider:</Typography>
                  </Grid>
                  <Grid item xs={8}>
                    <Typography>{provider?.formattedDisplayName || provider?.displayname || `${provider?.firstname} ${provider?.lastname}` || provider?.schedulingname}</Typography>
                  </Grid>
                  
                  {/* Show reason for visit */}
                  <Grid item xs={4}>
                    <Typography variant="subtitle2">{bookingType === 'iv' ? 'IV Service Type' : 'Reason for visit'}:</Typography>
                  </Grid>
                  <Grid item xs={8}>
                    <Typography>{reason?.reason || formData.reasonId}</Typography>
                  </Grid>
                  
                  <Grid item xs={4}>
                    <Typography variant="subtitle2">Date:</Typography>
                  </Grid>
                  <Grid item xs={8}>
                    <Typography>
                      {formData.appointmentDate?.toLocaleDateString()}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={4}>
                    <Typography variant="subtitle2">Time:</Typography>
                  </Grid>
                  <Grid item xs={8}>
                    <Typography>
                      {selectedSlot?.starttime || 
                        (formData.appointmentTime && 
                          `${formData.appointmentTime.getHours()}:${formData.appointmentTime.getMinutes().toString().padStart(2, '0')}`
                        )}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={4}>
                    <Typography variant="subtitle2">Patient:</Typography>
                  </Grid>
                  <Grid item xs={8}>
                    <Typography>
                      {patient ? `${patient.firstname} ${patient.lastname}` : formData.patientId}
                    </Typography>
                  </Grid>
                  
                  {formData.notes && (
                    <>
                      <Grid item xs={4}>
                        <Typography variant="subtitle2">Notes:</Typography>
                      </Grid>
                      <Grid item xs={8}>
                        <Typography>{formData.notes}</Typography>
                      </Grid>
                    </>
                  )}
                  
                  {patient && (
                    <>
                      <Grid item xs={4}>
                        <Typography variant="subtitle2">Patient Details:</Typography>
                      </Grid>
                      <Grid item xs={8}>
                        <Box sx={{ backgroundColor: '#f5f5f5', p: 1, borderRadius: 1 }}>
                          <Typography variant="body2" sx={{ mb: 0.5 }}>
                            <strong>DOB:</strong> {patient.dob && new Date(patient.dob).toLocaleDateString()}
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 0.5 }}>
                            <strong>Phone:</strong> {patient.mobilephone || 'Not provided'}
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 0.5 }}>
                            <strong>Email:</strong> {patient.email || 'Not provided'}
                          </Typography>
                          {patient.address1 && (
                            <Typography variant="body2" sx={{ mb: 0.5 }}>
                              <strong>Address:</strong> {patient.address1}
                              {patient.city && `, ${patient.city}`}
                              {patient.state && `, ${patient.state}`}
                              {patient.zip && ` ${patient.zip}`}
                            </Typography>
                          )}
                          <Typography variant="body2">
                            <strong>Patient ID:</strong> {patient.patientid}
                          </Typography>
                        </Box>
                      </Grid>
                    </>
                  )}
                </Grid>
              </Box>
            </Grid>
          </Grid>
        );
        
      default:
        return null;
    }
  };

  return (
    <>
      <Dialog 
        open={open} 
        onClose={onClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Book New Appointment
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
          
          {loading && activeStep !== 1 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            renderStepContent()
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          {activeStep > 0 && (
            <Button onClick={handleBack}>
              Back
            </Button>
          )}
          {activeStep < steps.length - 1 ? (
            <Button 
              onClick={handleNext} 
              variant="contained" 
              color="primary"
              disabled={loading}
            >
              Next
            </Button>
          ) : (
            <Button 
              onClick={handleSubmit} 
              variant="contained" 
              color="primary"
              disabled={loading}
            >
              Book Appointment
            </Button>
          )}
        </DialogActions>
      </Dialog>
      
      {/* Patient Form Dialog */}
      <PatientForm
        open={patientFormOpen}
        onClose={() => setPatientFormOpen(false)}
        onSave={handleSaveNewPatient}
      />
      
      {/* Snackbar notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AppointmentForm;
